import React, { useState } from 'react';
import './PaymentModal.css';
import { initiatePayUPayment } from '../utils/payu';
import { getPayUConfig, validatePayUConfig } from '../config/payu.config';

function PaymentModal({ course, onClose, onConfirm }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    collegeName: '',
    startDate: '',
    duration: '2' // Default to 2 months
  });

  const [errors, setErrors] = useState({});

  if (!course) return null;

  // Pricing constants
  const DURATION_2_MONTHS = '2';
  const DURATION_6_MONTHS = '6';
  const PRICE_2_MONTHS = 599;
  const PRICE_6_MONTHS = 999;

  // Calculate price based on selected duration
  const calculatePrice = () => {
    if (formData.duration === DURATION_6_MONTHS) {
      return PRICE_6_MONTHS;
    }
    return PRICE_2_MONTHS;
  };

  const finalPrice = calculatePrice();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/[- ]/g, ''))) {
      newErrors.mobile = 'Mobile number must be 10 digits';
    }
    
    if (!formData.collegeName.trim()) {
      newErrors.collegeName = 'College name is required';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Validate PayU configuration
      if (!validatePayUConfig()) {
        alert('Payment gateway configuration error. Please contact support.');
        return;
      }

      try {
        const payUConfig = getPayUConfig();
        
        // Prepare payment data for PayU
        const paymentData = {
          amount: finalPrice,
          productInfo: course.title,
          firstName: formData.name,
          email: formData.email,
          phone: formData.mobile,
          udf1: formData.collegeName,
          udf2: formData.startDate,
          udf3: course.id.toString(),
          udf4: `${formData.duration} months`,
          udf5: course.type
        };

        // Initiate PayU payment
        await initiatePayUPayment(paymentData, payUConfig);
        
        // Note: User will be redirected to PayU payment page
        // The response will be handled by the success/failure callback URLs
      } catch (error) {
        console.error('Payment initiation error:', error);
        alert('Failed to initiate payment. Please try again.');
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <h2 className="modal-title">Enroll in Course</h2>
        
        <div className="course-summary">
          <h3>{course.title}</h3>
          <div className="summary-details">
            <div className="detail-row">
              <span>Duration:</span>
              <span>{course.duration}</span>
            </div>
            <div className="detail-row">
              <span>Type:</span>
              <span>{course.type}</span>
            </div>
            <div className="detail-row total">
              <span>Total Amount:</span>
              <span className="amount">₹{finalPrice}</span>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleConfirm} className="enrollment-form">
          <div className="form-group">
            <label htmlFor="duration">Course Duration *</label>
            <select
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              className="duration-select"
            >
              <option value={DURATION_2_MONTHS}>2 Months - ₹{PRICE_2_MONTHS}</option>
              <option value={DURATION_6_MONTHS}>6 Months - ₹{PRICE_6_MONTHS}</option>
            </select>
            <p className="duration-info">Select your preferred course duration. 6-month course includes additional practice projects and mentorship.</p>
          </div>

          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
              placeholder="Enter your full name"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="mobile">Mobile Number *</label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              className={errors.mobile ? 'error' : ''}
              placeholder="Enter 10-digit mobile number"
            />
            {errors.mobile && <span className="error-message">{errors.mobile}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="collegeName">College Name *</label>
            <input
              type="text"
              id="collegeName"
              name="collegeName"
              value={formData.collegeName}
              onChange={handleChange}
              className={errors.collegeName ? 'error' : ''}
              placeholder="Enter your college name"
            />
            {errors.collegeName && <span className="error-message">{errors.collegeName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="startDate">Preferred Start Date *</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className={errors.startDate ? 'error' : ''}
              min={new Date().toISOString().split('T')[0]}
            />
            {errors.startDate && <span className="error-message">{errors.startDate}</span>}
          </div>
          
          <div className="payment-info">
            <p className="info-text">
              🔒 Secure payment powered by PayU Payment Gateway. 
              You will be redirected to PayU's secure payment page to complete your transaction.
            </p>
          </div>
          
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="confirm-button">
              Proceed to Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PaymentModal;
